import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * A textarea that grows with its text, so a long photo caption can be read
 * back while it is typed instead of scrolling inside two cramped lines.
 *
 * It used to measure itself only when the text changed. The first measure ran
 * before the web font had loaded (Inter sets wider than the fallback) and
 * never again when the card changed width, so the extra lines were cut off by
 * `overflow: hidden` — "only 2 or 3 lines visible". It now measures again
 * when its width changes and once the fonts are ready.
 */
export default function AutoGrowTextarea({ value, minRows = 5, className = '', ...props }) {
  const ref = useRef(null);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, []);

  useLayoutEffect(fit, [value, fit]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let width = el.clientWidth;
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        // Only a width change re-wraps the text; our own height change must
        // not loop back into another measure.
        if (el.clientWidth !== width) {
          width = el.clientWidth;
          fit();
        }
      });
      observer.observe(el);
    }
    let alive = true;
    document.fonts?.ready?.then(() => { if (alive) fit(); });
    return () => {
      alive = false;
      observer?.disconnect();
    };
  }, [fit]);

  return (
    <textarea
      ref={ref}
      rows={minRows}
      value={value}
      className={`autogrow ${className}`.trim()}
      {...props}
    />
  );
}
